import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  LinearProgress,
  Stack,
  useTheme,
  useMediaQuery,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { PRIMARY_COLOR_HEX, WHITE_HEX } from '../../../../theme';
import Lottie from 'lottie-react';

// Import company logos
import salesforceLogo from '../../../../assets/company-logos/salesforce.png';
import nytLogo from '../../../../assets/company-logos/nyt.png';
import capitaloneLogo from '../../../../assets/company-logos/capitalone.png';
import redfinLogo from '../../../../assets/company-logos/redfin.png';
import spotifyLogo from '../../../../assets/company-logos/spotify.png';
import googleLogo from '../../../../assets/company-logos/google.png';
import airbnbLogo from '../../../../assets/company-logos/airbnb.png';
import hlsLogo from '../../../../assets/company-logos/hls.png';
import LargeActionButton from '../../../common/LargeActionButton';

// Import avatar images
import RatingDisplay from './RatingDisplay';
import { useGetOnboardingSlideshowSteps } from './useGetOnboardingSlideshowSteps';
import { ONBOARDING_SLIDESHOW_ANIMATIONS } from './useGetOnboardingSlideshowSteps';
import { useHardAnnualOnlyTrialPaywall } from '../../../../hooks/useHardAnnualOnlyTrialPaywall';
import { AB_TESTS } from '../../../../services/abTesting/abTests';
import { useIsABTestActive } from '../../../../services/abTesting/useGetABTest';

export type OnboardingSlideshowProps = {
  next: () => void;
  nativeAppFunnelMode: boolean;
};

// Company logos mapping
const companyLogos = {
  salesforce: salesforceLogo,
  nyt: nytLogo,
  capitalone: capitaloneLogo,
  redfin: redfinLogo,
  spotify: spotifyLogo,
  google: googleLogo,
  airbnb: airbnbLogo,
  hls: hlsLogo,
};

function OnboardingSlideshow(props: OnboardingSlideshowProps) {
  const { next, nativeAppFunnelMode } = props;

  const { t } = useTranslation();
  const theme = useTheme();
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('xs'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const isWeb2AppFunnelTestActive = useIsABTestActive(AB_TESTS.WEB_2_APP_FUNNEL_SLIDESHOW);
  const { isHardAnnualOnlyTrialPaywallWithSlideshow } = useHardAnnualOnlyTrialPaywall();
  const shouldShowSlideshow = nativeAppFunnelMode
    ? isWeb2AppFunnelTestActive
    : isHardAnnualOnlyTrialPaywallWithSlideshow;

  const { steps, currentStepIndex, currentStep, currentAnswer, handleAnswer, handleNext } =
    useGetOnboardingSlideshowSteps({
      next,
      shouldShowSlideshow,
    });

  const renderProgressBar = () => (
    <Box sx={{ width: '80%', p: 2, display: 'flex', justifyContent: 'center', gap: 1 }}>
      {steps.map((_, index) => (
        <LinearProgress
          key={index}
          variant='determinate'
          value={index <= currentStepIndex ? 100 : 0}
          sx={{
            flex: 1,
            height: 4,
            borderRadius: 2,
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: PRIMARY_COLOR_HEX,
            },
          }}
        />
      ))}
    </Box>
  );

  const renderContent = () => {
    return (
      <>
        <Box sx={{ textAlign: 'center', mt: 2, px: 2, maxWidth: '800px', mx: 'auto' }}>
          <Typography
            variant='h4'
            component='h1'
            gutterBottom
            sx={{
              fontWeight: 'bold',
              fontSize: {
                xs: '1.75rem',
                sm: '2.25rem',
                md: '3rem',
              },
              lineHeight: 1.2,
              mb: { xs: 1, sm: 2 },
            }}
          >
            {currentStep.title}
          </Typography>
          {currentStep.showRatingDisplay && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
              <RatingDisplay />
            </Box>
          )}
          {currentStep.content && (
            <Typography
              variant='body1'
              color='text.secondary'
              sx={{
                fontSize: {
                  xs: '1.125rem',
                  sm: '1.25rem',
                  md: '1.5rem',
                },
                lineHeight: 1.5,
                maxWidth: '800px',
                mx: 'auto',
                mb: 4,
              }}
            >
              {currentStep.content}
            </Typography>
          )}
        </Box>

        {currentStep.isQuestion && currentStep.questionOptions && (
          <Box
            sx={{
              width: '90%',
              maxWidth: '600px',
              mx: 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              pt: isExtraSmall ? 1 : isSmall ? 4 : 4,
            }}
          >
            <FormControl component='fieldset' sx={{ width: '100%' }}>
              <RadioGroup
                aria-label='options'
                name='row-radio-buttons-group'
                value={currentAnswer || ''}
                onChange={(e) => handleAnswer(e.target.value)}
              >
                {currentStep.questionOptions.map((option, i) => (
                  <FormControlLabel
                    key={i}
                    value={option.value}
                    label={option.label}
                    control={
                      <Radio
                        sx={{
                          '& .MuiSvgIcon-root': {
                            fontSize: { xs: 28, sm: 32 },
                            color: currentAnswer === option.value ? WHITE_HEX : 'inherit',
                          },
                          padding: { xs: 1.75, sm: 2.25 },
                        }}
                      />
                    }
                    sx={{
                      width: '100%',
                      height: { xs: 72, sm: 88 },
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      margin: '6px 0',
                      backgroundColor:
                        currentAnswer === option.value ? PRIMARY_COLOR_HEX : 'background.paper',
                      '& .MuiFormControlLabel-label': {
                        fontSize: { xs: '1.125rem', sm: '1.375rem' },
                        fontWeight: 500,
                        color: currentAnswer === option.value ? WHITE_HEX : 'text.primary',
                      },
                    }}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Box>
        )}

        {currentStep.lottie && (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              minHeight: 0,
              maxWidth: {
                xs: '80%',
                sm: 600,
              },
              mx: 'auto',
            }}
          >
            <Lottie
              animationData={ONBOARDING_SLIDESHOW_ANIMATIONS[currentStep.lottie]}
              loop
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          </Box>
        )}

        {currentStep.bullets && (
          <Stack spacing={2} sx={{ mt: 'auto', mb: isSmall ? 4 : 8 }}>
            {currentStep.bullets.map((bullet: string | JSX.Element, index: number) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <CheckCircleIcon
                  sx={{
                    color: PRIMARY_COLOR_HEX,
                    fontSize: isExtraSmall ? '1.5rem' : '1.75rem',
                  }}
                />
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: isExtraSmall ? '1rem' : '1rem',
                    lineHeight: 1.5,
                  }}
                >
                  {bullet}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}

        {currentStep.showCompanyLogos && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-evenly',
              width: '100%',
            }}
          >
            <Box sx={{ px: isSmall ? 4 : 12, textAlign: 'center' }}>
              <Typography
                sx={{
                  mb: isSmall ? 3 : 6,
                  fontSize: isSmall ? '1rem' : '1.5rem',
                }}
              >
                {t('onboarding.step3.trustedBy')}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))',
                  gap: isSmall ? '18px 28px' : '28px 48px',
                  justifyContent: 'center',
                }}
              >
                {Object.entries(companyLogos)
                  .filter(
                    ([company]) => !isSmall || (company !== 'spotify' && company !== 'redfin')
                  )
                  .map(([company, logo]) => (
                    <Box
                      key={company}
                      component='img'
                      src={logo}
                      alt={company}
                      sx={{
                        width: '100%',
                        height: isSmall ? 28 : 40,
                        objectFit: 'contain',
                        filter: 'grayscale(100%)',
                        px: isSmall ? 1 : 2,
                      }}
                    />
                  ))}
              </Box>
            </Box>
          </Box>
        )}

        <LargeActionButton
          marginBottom={isSmall ? 4 : 4}
          width='90%'
          maxWidth='600px'
          marginTop={currentStep.isQuestion ? '8px' : 0}
          onClick={handleNext}
          disabled={currentStep.isQuestion && !currentAnswer}
        >
          {t('next')}
        </LargeActionButton>
      </>
    );
  };

  return (
    <>
      {renderProgressBar()}
      {renderContent()}
    </>
  );
}

export default OnboardingSlideshow;
